/**
 * SKU Slice
 * Redux Toolkit slice for SKU state management
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { SKU, CreateSKURequest, UpdateSKURequest } from '@creatoros/shared';
import { skuService, SKUWithUserCount, SKUUser } from '../../services/sku.service';
import { logger } from '../../utils/logger';

/**
 * SKU State Interface
 */
export interface SKUState {
  skus: SKUWithUserCount[];
  selectedSku: SKUWithUserCount | null;
  skuUsers: SKUUser[];
  skuUsersTotal: number;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isLoadingUsers: boolean;
  error: string | null;
}

/**
 * Initial State
 */
const initialState: SKUState = {
  skus: [],
  selectedSku: null,
  skuUsers: [],
  skuUsersTotal: 0,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isLoadingUsers: false,
  error: null,
};

/**
 * Async Thunks
 */

/**
 * Fetch all SKUs
 */
export const fetchSkus = createAsyncThunk<
  SKUWithUserCount[],
  { includeUserCount?: boolean } | void,
  { rejectValue: string }
>('skus/fetchAll', async (params, { rejectWithValue }) => {
  try {
    logger.debug('Fetching SKUs', { event: 'skus.fetch.started' });
    const response = await skuService.list({
      includeUserCount: params?.includeUserCount ?? true,
    });
    logger.debug('Fetched SKUs', {
      event: 'skus.fetch.succeeded',
      count: response.skus.length,
    });
    return response.skus;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    logger.error('Failed to fetch SKUs', error, { event: 'skus.fetch.failed' });
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch SKUs');
  }
});

/**
 * Fetch a single SKU by ID
 */
export const fetchSkuById = createAsyncThunk<SKUWithUserCount, string, { rejectValue: string }>(
  'skus/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      logger.debug('Fetching SKU', { event: 'skus.fetch_by_id.started', skuId: id });
      const sku = await skuService.getById(id);
      logger.debug('Fetched SKU', { event: 'skus.fetch_by_id.succeeded', skuId: id });
      return sku;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      logger.error('Failed to fetch SKU', error, { event: 'skus.fetch_by_id.failed', skuId: id });
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch SKU');
    }
  }
);

/**
 * Create a new SKU
 */
export const createSku = createAsyncThunk<SKU, CreateSKURequest, { rejectValue: string }>(
  'skus/create',
  async (data, { rejectWithValue }) => {
    try {
      const sku = await skuService.create(data);
      logger.info('SKU created', { event: 'skus.create.succeeded', skuId: sku.id });
      return sku;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      logger.error('Failed to create SKU', error, { event: 'skus.create.failed' });
      return rejectWithValue(err.response?.data?.message || 'Failed to create SKU');
    }
  }
);

/**
 * Update an existing SKU
 */
export const updateSku = createAsyncThunk<
  SKU,
  { id: string; data: UpdateSKURequest },
  { rejectValue: string }
>('skus/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const sku = await skuService.update(id, data);
    logger.info('SKU updated', { event: 'skus.update.succeeded', skuId: id });
    return sku;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    logger.error('Failed to update SKU', error, { event: 'skus.update.failed', skuId: id });
    return rejectWithValue(err.response?.data?.message || 'Failed to update SKU');
  }
});

/**
 * Deactivate a SKU
 */
export const deactivateSku = createAsyncThunk<SKU, string, { rejectValue: string }>(
  'skus/deactivate',
  async (id, { rejectWithValue }) => {
    try {
      const sku = await skuService.deactivate(id);
      logger.info('SKU deactivated', { event: 'skus.deactivate.succeeded', skuId: id });
      return sku;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      logger.error('Failed to deactivate SKU', error, {
        event: 'skus.deactivate.failed',
        skuId: id,
      });
      return rejectWithValue(err.response?.data?.message || 'Failed to deactivate SKU');
    }
  }
);

/**
 * Fetch users on a SKU
 */
export const fetchSkuUsers = createAsyncThunk<
  { users: SKUUser[]; total: number },
  { skuId: string; limit?: number },
  { rejectValue: string }
>('skus/fetchUsers', async ({ skuId, limit }, { rejectWithValue }) => {
  try {
    logger.debug('Fetching SKU users', { event: 'skus.fetch_users.started', skuId });
    const response = await skuService.getUsers(skuId, { limit });
    logger.debug('Fetched SKU users', {
      event: 'skus.fetch_users.succeeded',
      skuId,
      count: response.users.length,
    });
    return { users: response.users, total: response.total };
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    logger.error('Failed to fetch SKU users', error, { event: 'skus.fetch_users.failed', skuId });
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch users');
  }
});

/**
 * SKU Slice
 */
const skuSlice = createSlice({
  name: 'skus',
  initialState,
  reducers: {
    /**
     * Set selected SKU
     */
    setSelectedSku: (state, action: PayloadAction<SKUWithUserCount | null>) => {
      state.selectedSku = action.payload;
    },

    /**
     * Clear selected SKU
     */
    clearSelectedSku: (state) => {
      state.selectedSku = null;
      state.skuUsers = [];
      state.skuUsersTotal = 0;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Reset state
     */
    resetSkuState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all SKUs
      .addCase(fetchSkus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSkus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.skus = action.payload;
      })
      .addCase(fetchSkus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch SKUs';
      })

      // Fetch SKU by ID
      .addCase(fetchSkuById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSkuById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedSku = action.payload;
        // Update in list if exists
        const index = state.skus.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.skus[index] = action.payload;
        }
      })
      .addCase(fetchSkuById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch SKU';
      })

      // Create SKU
      .addCase(createSku.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createSku.fulfilled, (state, action) => {
        state.isCreating = false;
        state.skus.push({ ...action.payload, userCount: 0 });
      })
      .addCase(createSku.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? 'Failed to create SKU';
      })

      // Update SKU
      .addCase(updateSku.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateSku.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.skus.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          // Preserve userCount from existing SKU since update doesn't return it
          const existingUserCount = state.skus[index]?.userCount ?? 0;
          state.skus[index] = { ...action.payload, userCount: existingUserCount };
        }
        if (state.selectedSku?.id === action.payload.id) {
          const existingUserCount = state.selectedSku.userCount;
          state.selectedSku = { ...action.payload, userCount: existingUserCount };
        }
      })
      .addCase(updateSku.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? 'Failed to update SKU';
      })

      // Deactivate SKU
      .addCase(deactivateSku.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deactivateSku.fulfilled, (state, action) => {
        state.isDeleting = false;
        const index = state.skus.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          // Preserve userCount from existing SKU since deactivate doesn't return it
          const existingUserCount = state.skus[index]?.userCount ?? 0;
          state.skus[index] = { ...action.payload, userCount: existingUserCount };
        }
        if (state.selectedSku?.id === action.payload.id) {
          const existingUserCount = state.selectedSku.userCount;
          state.selectedSku = { ...action.payload, userCount: existingUserCount };
        }
      })
      .addCase(deactivateSku.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Failed to deactivate SKU';
      })

      // Fetch SKU users
      .addCase(fetchSkuUsers.pending, (state) => {
        state.isLoadingUsers = true;
        state.error = null;
        // Clear stale users from a previous SKU so the panel never shows
        // data from a different SKU while the new fetch is in flight.
        state.skuUsers = [];
        state.skuUsersTotal = 0;
      })
      .addCase(fetchSkuUsers.fulfilled, (state, action) => {
        state.isLoadingUsers = false;
        // Guard against a missing/null `users` field in the API response
        // (e.g., serialization edge-case for empty result sets) to prevent
        // a TypeError crash when the panel renders skuUsers.length.
        state.skuUsers = action.payload.users ?? [];
        state.skuUsersTotal = action.payload.total ?? 0;
      })
      .addCase(fetchSkuUsers.rejected, (state, action) => {
        state.isLoadingUsers = false;
        state.skuUsers = [];
        state.skuUsersTotal = 0;
        state.error = action.payload ?? 'Failed to fetch users';
      });
  },
});

/**
 * Selectors
 */

// Base selectors
export const selectSkus = (state: RootState) => state.skus.skus;
export const selectSelectedSku = (state: RootState) => state.skus.selectedSku;
export const selectSkuUsers = (state: RootState) => state.skus.skuUsers;
export const selectSkuUsersTotal = (state: RootState) => state.skus.skuUsersTotal;
export const selectSkuError = (state: RootState) => state.skus.error;
export const selectSkuIsLoading = (state: RootState) => state.skus.isLoading;
export const selectSkuIsCreating = (state: RootState) => state.skus.isCreating;
export const selectSkuIsUpdating = (state: RootState) => state.skus.isUpdating;
export const selectSkuIsDeleting = (state: RootState) => state.skus.isDeleting;
export const selectSkuIsLoadingUsers = (state: RootState) => state.skus.isLoadingUsers;

// Derived selectors
export const selectSkuById = (state: RootState, id: string) =>
  state.skus.skus.find((sku) => sku.id === id);

export const selectActiveSkus = (state: RootState) => state.skus.skus.filter((sku) => sku.isActive);

export const selectPublicSkus = (state: RootState) =>
  state.skus.skus.filter((sku) => sku.isPublic && sku.isActive);

export const selectSkusCount = (state: RootState) => state.skus.skus.length;

export const selectTotalSkuRevenue = (state: RootState) =>
  state.skus.skus.reduce((total, sku) => total + sku.pricing.monthly * sku.userCount, 0);

/**
 * Export actions and reducer
 */
export const { setSelectedSku, clearSelectedSku, clearError, resetSkuState } = skuSlice.actions;

export default skuSlice.reducer;
